import React, { useState, useEffect } from "react";
import {
  X,
  Phone,
  Mail,
  MapPin,
  MessageCircle,
  Calendar,
  Building,
  ShieldCheck,
  Loader2,
  Trash2,
  Edit,
} from "lucide-react";
import { getNetworkPartnerById } from "./networkApi";

const PartnerDetailsDrawer = ({ show, partnerId, initialPartner, onClose, onEdit, onDelete }) => {
  const [partner, setPartner] = useState(initialPartner || null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (initialPartner) {
      setPartner(initialPartner);
    }
  }, [initialPartner]);

  useEffect(() => {
    if (show && partnerId) {
      const loadDetails = async () => {
        try {
          setIsLoading(true);
          const res = await getNetworkPartnerById(partnerId);
          if (res?.network || res?.partner || res?.user) {
            setPartner(res.network || res.partner || res.user);
          }
        } catch (err) {
          console.error("Failed to load partner details:", err);
        } finally {
          setIsLoading(false);
        }
      };
      loadDetails();
    }
  }, [show, partnerId]);

  if (!show) return null;

  const fullName = `${partner?.first_name || partner?.firstName || ""} ${partner?.last_name || partner?.lastName || ""}`.trim() || "Network Partner";
  const mobile = partner?.mobile_number || partner?.mobileNo || "";
  const email = partner?.email_address || partner?.emailId || "";
  const city = partner?.city_name || partner?.city || "";
  const state = partner?.state_name || partner?.state || "";
  const area = partner?.area_name || partner?.area || "";
  const address = partner?.address || "";
  const cleanPhone = mobile.replace(/^91/, "");
  const whatsappUrl = cleanPhone ? `https://wa.me/91${cleanPhone}?text=Hi%20${encodeURIComponent(fullName)}` : null;

  return (
    <div className="net-drawer-overlay" onClick={onClose}>
      <div className="net-drawer" onClick={(e) => e.stopPropagation()}>
        <div className="net-drawer__header">
          <h3 className="net-drawer__title">Partner Profile</h3>
          <button type="button" className="net-drawer__close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div className="net-drawer__body">
          {isLoading ? (
            <div className="net-loading-wrap">
              <Loader2 size={32} className="net-spin" style={{ color: "#2563eb" }} />
              <p>Loading partner details...</p>
            </div>
          ) : (
            <>
              {/* Profile Hero */}
              <div className="net-drawer-profile-hero">
                <div className="net-drawer-avatar-lg">
                  {fullName.charAt(0).toUpperCase()}
                </div>
                <h2 style={{ fontSize: "1.25rem", fontWeight: 800, color: "#0f172a", margin: "0 0 0.35rem 0" }}>
                  {fullName}
                </h2>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5rem" }}>
                  <span
                    style={{
                      background: "#eff6ff",
                      color: "#2563eb",
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      padding: "0.2rem 0.65rem",
                      borderRadius: "9999px",
                      border: "1px solid #bfdbfe",
                      textTransform: "uppercase",
                    }}
                  >
                    Verified Network Partner
                  </span>
                </div>

                {/* Quick action buttons */}
                <div style={{ display: "flex", gap: "0.65rem", justifyContent: "center", marginTop: "1.25rem" }}>
                  {mobile && (
                    <a href={`tel:${mobile}`} className="net-quick-btn" style={{ flex: "none", padding: "0.5rem 1rem" }}>
                      <Phone size={14} /> Call
                    </a>
                  )}
                  {whatsappUrl && (
                    <a
                      href={whatsappUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="net-quick-btn net-quick-btn--wa"
                      style={{ flex: "none", padding: "0.5rem 1rem" }}
                    >
                      <MessageCircle size={14} /> WhatsApp
                    </a>
                  )}
                  {email && (
                    <a href={`mailto:${email}`} className="net-quick-btn" style={{ flex: "none", padding: "0.5rem 1rem" }}>
                      <Mail size={14} /> Email
                    </a>
                  )}
                </div>
              </div>

              {/* Information Grid */}
              <h4 className="net-drawer-section-title">Contact & Location</h4>
              <div className="net-drawer-item-grid">
                <div className="net-drawer-item">
                  <div className="net-drawer-item__lbl">Phone Number</div>
                  <div className="net-drawer-item__val" style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                    <Phone size={14} style={{ color: "#2563eb" }} /> +91 {cleanPhone || "Not provided"}
                  </div>
                </div>

                <div className="net-drawer-item">
                  <div className="net-drawer-item__lbl">Email Address</div>
                  <div className="net-drawer-item__val" style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                    <Mail size={14} style={{ color: "#2563eb" }} /> {email || "Not provided"}
                  </div>
                </div>

                <div className="net-drawer-item">
                  <div className="net-drawer-item__lbl">City & State</div>
                  <div className="net-drawer-item__val" style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                    <MapPin size={14} style={{ color: "#2563eb" }} />
                    {[city, state].filter(Boolean).join(", ") || "India"}
                  </div>
                </div>

                {area && (
                  <div className="net-drawer-item">
                    <div className="net-drawer-item__lbl">Area / Locality</div>
                    <div className="net-drawer-item__val">{area}</div>
                  </div>
                )}

                {address && (
                  <div className="net-drawer-item">
                    <div className="net-drawer-item__lbl">Street Address</div>
                    <div className="net-drawer-item__val">{address}</div>
                  </div>
                )}

                <div className="net-drawer-item">
                  <div className="net-drawer-item__lbl">Network Status</div>
                  <div className="net-drawer-item__val" style={{ display: "flex", alignItems: "center", gap: "0.4rem", color: "#16a34a" }}>
                    <ShieldCheck size={15} /> Active Associate Connection
                  </div>
                </div>
              </div>

              {/* Drawer Action Bar */}
              <div
                style={{
                  display: "flex",
                  gap: "0.75rem",
                  marginTop: "2rem",
                  paddingTop: "1.25rem",
                  borderTop: "1px solid #f1f5f9",
                }}
              >
                <button
                  type="button"
                  className="net-btn net-btn--secondary"
                  style={{ flex: 1 }}
                  onClick={() => {
                    onClose?.();
                    onEdit?.(partner);
                  }}
                >
                  <Edit size={14} /> Edit Partner
                </button>
                <button
                  type="button"
                  className="net-btn net-btn--danger"
                  style={{ flex: 1 }}
                  onClick={() => {
                    onClose?.();
                    onDelete?.(partner);
                  }}
                >
                  <Trash2 size={14} /> Remove
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PartnerDetailsDrawer;
